import { useMemo, useRef } from "react";
import { cx } from "../../../utils";
import { Show } from "../../helpers/Show";

export enum RadioSize {
  BASE = "BASE",
  LG = "LG"
}

export interface RadioProps {
  /**
   * Additional classes applied to the root label element.
   * Controls layout, spacing, typography, and cursor style.
   */
  className?: string;

  /**
   * Classes applied to the label wrapper (text and error container).
   * Useful for controlling label text layout and spacing.
   */
  labelClassName?: string;

  /**
   * Classes applied to the styled radio circle container.
   * Controls size, border, colors, hover effects, and transition.
   */
  checkboxClassName?: string;

  /** Text or JSX content displayed next to the radio circle. */
  label?: string | JSX.Element;

  /** Text or JSX content displayed next to the radio circle under the label.
   * WORKS only if label is string
   */
  subLabelText?: string;

  disabled?: boolean;
  id: string;
  name: string;
  checked?: boolean;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  error?: boolean | string;
  size?: RadioSize;
}

const radioInnerSize = (size: RadioSize) => {
  switch (size) {
    case RadioSize.LG:
      return "size-2";
    default:
      return "size-1.5";
  }
};

const radioOuterSize = (size: RadioSize) => {
  switch (size) {
    case RadioSize.LG:
      return "size-5";
    default:
      return "size-4";
  }
};

export const Radio = ({
  label,
  subLabelText,
  className,
  labelClassName,
  checkboxClassName,
  disabled,
  id,
  checked,
  onChange,
  error,
  size = RadioSize.BASE,
  ...props
}: RadioProps) => {
  const radioRef = useRef<HTMLInputElement>(null);

  const checkboxClasses = useMemo(() => {
    if (disabled) {
      if (error) return "border-danger-500 bg-alpha-40";
      return "border-content-100 bg-alpha-40";
    }

    if (error)
      return "border-danger-500 bg-white hover:border-danger-600 hover:bg-danger-20";

    if (checked)
      return "border-primary-500 bg-primary-20 hover:border-primary-500 hover:bg-primary-20";

    return "border-content-100 bg-white hover:border-primary-500 hover:bg-primary-20";
  }, [checked, disabled, error]);

  const outerCircleClasses = useMemo(() => {
    return cx(
      "size-4 rounded-full border flex items-center justify-center transition-colors shrink-0",
      radioOuterSize(size),
      checkboxClasses,
      checkboxClassName
    );
  }, [checkboxClasses, checkboxClassName, size]);

  const innerCircleClasses = useMemo(() => {
    return cx(
      "rounded-full transition-opacity",
      disabled ? "bg-content-100" : "bg-primary-500",
      checked ? "opacity-100" : "opacity-0",
      radioInnerSize(size)
    );
  }, [disabled, checked, size]);

  const labelClasses = useMemo(() => {
    return cx(size === RadioSize.BASE ? "-mt-1" : "-mt-0.5", labelClassName);
  }, [labelClassName, size]);

  const wrapperClasses = useMemo(() => {
    return cx(
      "flex items-start text-base font-medium cursor-pointer relative",
      disabled ? "cursor-not-allowed text-content-100" : "text-content-700",
      size === RadioSize.BASE ? "gap-2" : "gap-3",
      className
    );
  }, [disabled, className, size]);

  return (
    <label htmlFor={id} className={wrapperClasses}>
      <input
        id={id}
        type="radio"
        className="sr-only"
        disabled={disabled}
        checked={checked}
        onChange={onChange}
        ref={radioRef}
        {...props}
      />

      <div
        className={outerCircleClasses}
        role="radio"
        aria-checked={checked}
        tabIndex={0} // Make div focusable for keyboard, sync with input focus
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            radioRef.current?.click();
          }
        }}
      >
        <div className={innerCircleClasses} />
      </div>

      {label ? (
        <div className={labelClasses}>
          <p>{label}</p>

          <Show if={subLabelText}>
            <p className="text-content-400 text-sm font-normal">
              {subLabelText}
            </p>
          </Show>

          {error && typeof error === "string" ? (
            <p
              id={`${id}-error`}
              className="text-danger-500 font-regular text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </label>
  );
};
